<template>
  <div v-cloak>
    <!-- Hero Section-->
    <section class="hero hero-page gray-bg padding-small">
      <div class="container">
        <div class="row d-flex">
          <div class="col-lg-9 order-2 order-lg-1">
            <h1>
              {{ styleName }}&nbsp;&nbsp;
              <img alt="icon" width="145" height="145" :src="iconImage" />
            </h1>
            <h5>{{ $t('Solids and Prints') }}</h5>
            <p class="lead text-muted">{{ $t('ChooseAnyPrint') }}</p>
            <div v-if="isAuthenticated" class="list-inline-item">
              <FavoriteButton
                :is-favorite="isFavorite"
                item-type="style"
                :item-id="styleId"
                :xsrf-token="xsrfToken"
              />
            </div>
          </div>
          <div class="col-lg-3 text-right order-1 order-lg-2">
            <ul class="breadcrumb justify-content-lg-end">
              <li class="breadcrumb-item">
                <a href="/">{{ $t('Home') }}</a>
              </li>
              <li class="breadcrumb-item">
                <a href="/styles">{{ $t('Styles') }}</a>
              </li>
              <li class="breadcrumb-item active">
                {{ styleName }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <main id="style-with-prints-list">
      <div class="container">
        <div class="row">
          <!-- Sidebar-->
          <category-filter
            v-model="filterValue"
            :categories="categories"
          ></category-filter>
          <!-- /Sidebar end-->
          <!-- Grid -->
          <div class="products-grid col-xl-9 col-lg-8 sidebar-left">
            <!--SOLIDS-->
            <div v-if="solidFinalPrice && solidDesigns.length > 0">
              <div class="row">
                <div class="col-sm-12">
                  <p class="text-center">
                    <span class="h3">{{ $t('Solids') }}</span>
                    <span class="h5 text-primary"
                      >&nbsp; {{ asCurrency(solidFinalPrice.amount) }}
                      <small v-if="currency !== displayCurrency">
                        {{ asDisplayCurrency(solidFinalPrice.amount) }}</small
                      ></span
                    >
                    <span
                      v-if="solidFinalPrice.amount !== solidRetailPrice.amount"
                      class="h6 text-black-50"
                      >&nbsp;<s>
                        {{ asCurrency(solidRetailPrice.amount) }}
                        <small v-if="currency !== displayCurrency"
                          ><s>
                            {{ asDisplayCurrency(solidRetailPrice.amount) }}</s
                          ></small
                        ></s
                      ></span
                    >
                  </p>
                </div>
              </div>
              <item-count
                v-model="solidItemsPerPage"
                :count="filteredSolids.length"
                :total="solidDesigns?.length ?? 0"
              ></item-count>
              <div class="row">
                <!-- item-->
                <div
                  v-for="item in filteredSolids"
                  :key="item.id"
                  class="item col-xl-3 col-md-6 col-xs-6"
                >
                  <style-with-print-item
                    :item="item"
                    :style-id="styleId"
                    :culture="culture"
                    :blob-domain="blobDomain"
                  ></style-with-print-item>
                </div>
              </div>
              <show-more-button v-model="solidItemsPerPage"></show-more-button>
              <item-count
                v-model="solidItemsPerPage"
                :count="filteredSolids.length"
                :total="solidDesigns.length"
              ></item-count>
            </div>

            <!--PRINTS-->
            <div v-if="repeatFinalPrice && repeatPrintDesigns.length > 0">
              <div class="row">
                <div class="col-sm-12">
                  <p class="text-center">
                    <span class="h3">{{ $t('Prints') }}</span>
                    <span class="h5 text-primary"
                      >&nbsp; {{ asCurrency(repeatFinalPrice.amount) }}
                      <small v-if="currency !== displayCurrency">
                        {{ asDisplayCurrency(repeatFinalPrice.amount) }}</small
                      ></span
                    >
                    <span
                      v-if="
                        repeatFinalPrice.amount !== repeatRetailPrice.amount
                      "
                      class="h6 text-black-50"
                      >&nbsp;<s>
                        {{ asCurrency(repeatRetailPrice.amount) }}
                        <small v-if="currency !== displayCurrency">
                          ><s>
                            {{ asDisplayCurrency(repeatRetailPrice.amount) }}</s
                          ></small
                        ></s
                      ></span
                    >
                  </p>
                </div>
              </div>
              <item-count
                v-model="printItemsPerPage"
                :count="filteredPrints.length"
                :total="repeatPrintDesigns.length"
              ></item-count>
              <div class="row">
                <!-- item-->
                <div
                  v-for="item in filteredPrints"
                  :key="item.id"
                  class="item col-xl-3 col-md-6 col-xs-6"
                >
                  <style-with-print-item
                    :item="item"
                    :style-id="styleId"
                    :culture="culture"
                    :blob-domain="blobDomain"
                  ></style-with-print-item>
                </div>
              </div>
              <show-more-button v-model="printItemsPerPage"></show-more-button>
              <item-count
                v-model="printItemsPerPage"
                :count="filteredPrints.length"
                :total="repeatPrintDesigns?.length ?? 0"
              ></item-count>
            </div>
          </div>
          <!-- / Grid End-->
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import type Category from '../../common/Category';
import type { CatalogItem } from '../../common/CatalogItem';
import {
  asCurrency as asCurrencyUtil,
  asDisplayCurrency as asDisplayCurrencyUtil,
  type MoneyDto,
} from '../../common/currency';
import StyleWithPrintItem from '../../components/StyleWithPrintItem.vue';
import FavoriteButton from '../../components/FavoriteButton.vue';
import CategoryFilter from '../../components/CategoryFilter.vue';
import ItemCount from '../../components/ItemCount.vue';
import ShowMoreButton from '../../components/ShowMoreButton.vue';
import { filter, intersection, isEmpty, take } from 'lodash-es';
import { computed, ref } from 'vue';

const props = defineProps<{
  categories?: Category[];
  currency?: string;
  displayCurrency?: string;
  displayExchangeRate?: number;
  culture?: string;
  blobDomain?: string;
  isFavorite?: boolean;
  styleId?: string;
  styleName?: string;
  iconImage?: string;
  isAuthenticated?: boolean;
  repeatPrintDesigns?: CatalogItem[];
  repeatRetailPrice?: MoneyDto;
  repeatFinalPrice?: MoneyDto;
  solidDesigns?: CatalogItem[];
  solidRetailPrice?: MoneyDto;
  solidFinalPrice?: MoneyDto;
  xsrfToken: string;
}>();

const urlParams = new URLSearchParams(window.location.search);

const filterData = ref<Record<string, string[]>>(
  JSON.parse(urlParams.get('filter') || '{}'),
);
const solidItemsPerPageData = ref<number>(
  Number(urlParams.get('solidItemsPerPage')) || 4,
);
const printItemsPerPageData = ref<number>(
  Number(urlParams.get('printItemsPerPage')) || 4,
);

const filterValue = computed<Record<string, string[]>>({
  get: () => filterData.value,
  set: (newValue) => {
    const cleaned = Object.fromEntries(
      Object.entries(newValue).filter(([, v]) => v.length > 0),
    );
    filterData.value = cleaned;
    const url = new URL(window.location.toString());
    url.searchParams.set('filter', JSON.stringify(cleaned));
    history.replaceState({}, '', url.toString());
  },
});

const solidItemsPerPage = computed<number>({
  get: () => solidItemsPerPageData.value,
  set: (newValue) => {
    solidItemsPerPageData.value = newValue;
    const url = new URL(window.location.toString());
    url.searchParams.set('solidItemsPerPage', newValue.toString());
    history.replaceState({}, '', url.toString());
  },
});

const printItemsPerPage = computed<number>({
  get: () => printItemsPerPageData.value,
  set: (newValue) => {
    printItemsPerPageData.value = newValue;
    const url = new URL(window.location.toString());
    url.searchParams.set('printItemsPerPage', newValue.toString());
    history.replaceState({}, '', url.toString());
  },
});

const filteredSolids = computed(() =>
  take(props.solidDesigns, solidItemsPerPage.value),
);

const filteredPrints = computed(() => {
  if (isEmpty(filterValue.value)) {
    return take(props.repeatPrintDesigns, printItemsPerPage.value);
  }
  const nonEmptyCategories = filter(
    filterValue.value,
    (category) => category.length > 0,
  );
  if (nonEmptyCategories.length === 0) {
    return take(props.repeatPrintDesigns, printItemsPerPage.value);
  }
  return take(
    props.repeatPrintDesigns?.filter((item) =>
      nonEmptyCategories.some((selectedChildren) => {
        if (selectedChildren.length === 0) return true;
        return intersection(item.categories, selectedChildren).length > 0;
      }),
    ),
    printItemsPerPage.value,
  );
});

function asCurrency(amount: number): string {
  return asCurrencyUtil(props.currency, amount);
}

function asDisplayCurrency(amount: number): string {
  return asDisplayCurrencyUtil(
    props.displayExchangeRate,
    props.displayCurrency,
    amount,
  );
}
</script>
